import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Table } from 'src/design/molecules/Table';
import React from 'react';
import { useContactsByCustomer } from 'src/api/queries/organization/useContactsByCustomer';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { EmptyState } from 'src/design/atoms/EmptyState';

const naiveFormatPhoneNumber = (phone: string) => {
  if (!phone) return '';
  if (phone.length === 11)
    return phone.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4');
  if (phone.length === 10)
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  if (phone.length === 7) return phone.replace(/(\d{3})(\d{4})/, '$1-$2');
  return phone;
};

interface ContactsCardProps {
  jobId: number;
  customerNumber?: string;
  customerName?: string;
}

export const ContactsCard: React.FC<ContactsCardProps> = ({
  jobId,
  customerNumber,
  customerName,
}) => {
  const { data, isLoading } = useContactsByCustomer(customerNumber);
  const contacts = data ?? [];

  const renderContacts = () => {
    if (contacts.length === 0) {
      return (
        <EmptyState
          header="No Contacts"
          description="No contacts have been added to this job"
        />
      );
    }

    const rows = contacts.map((contact) => (
      <Table.Tr key={contact.id}>
        <Table.Td>
          {contact.firstName} {contact.lastName}
        </Table.Td>
        <Table.Td>{naiveFormatPhoneNumber(contact.phone)}</Table.Td>
        <Table.Td>{contact.email}</Table.Td>
      </Table.Tr>
    ));

    return (
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Phone</Table.Th>
            <Table.Th>Email</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
    );
  };

  return (
    <Paper pos="relative">
      <LoadingOverlay visible={isLoading} />
      <Stack>
        <Group justify="space-between">
          <Title order={5}>Contacts for {customerName?.trim()}</Title>
          <LinkButton size="compact-xs" to={`/jobs/${jobId}/contacts`}>
            Open
          </LinkButton>
        </Group>
        <Text>Contacts are shared across all jobs for this customer.</Text>
        <div style={{ height: 200, overflow: 'auto' }}>{renderContacts()}</div>
      </Stack>
    </Paper>
  );
};
